/**
 * Care Concierge session credit — shared admin widget (Feature 106).
 *
 * Mounted twice against the same endpoints: on the organization editor for a firm's shared pool,
 * and on the advisor editor for that account's own. Action-based rather than form fields, so
 * credit can never be changed as a side effect of saving a profile — same reasoning as
 * AdvisorTrialSection, and here it also keeps the ledger append-only.
 */

import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { useToast } from '@/shared/components/Toast';
import { SessionGrantList } from './SessionGrantList';
import {
  getSessionCredit,
  grantSessions,
  reverseSessionGrant,
  type CreditOwnerType,
  type SessionCredit,
} from '../../services/careConciergeCreditApi';

type SessionCreditSectionProps = {
  ownerType: CreditOwnerType;
  ownerId: string;
};

const errorMessage = (error: unknown, fallback: string): string =>
  (error as { response?: { data?: { message?: string } }; message?: string })?.response?.data
    ?.message ??
  (error as { message?: string })?.message ??
  fallback;

export const SessionCreditSection = ({ ownerType, ownerId }: SessionCreditSectionProps) => {
  const { showToast } = useToast();
  const [credit, setCredit] = useState<SessionCredit | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [sessions, setSessions] = useState('');
  const [note, setNote] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [busyGrantId, setBusyGrantId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setCredit(await getSessionCredit(ownerType, ownerId));
      setLoadError(null);
    } catch (error) {
      setLoadError(errorMessage(error, 'Could not load session credit.'));
    }
  }, [ownerType, ownerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleGrant = async () => {
    const parsed = Number(sessions);

    if (!Number.isInteger(parsed) || parsed < 1) {
      showToast('Enter a whole number of sessions, at least 1.', 'error');
      return;
    }

    setIsAdding(true);
    try {
      await grantSessions({
        ownerType,
        ownerId,
        sessions: parsed,
        note: note.trim() || undefined,
      });
      setSessions('');
      setNote('');
      await load();
      showToast(`Granted ${parsed} session${parsed === 1 ? '' : 's'}.`, 'success');
    } catch (error) {
      showToast(errorMessage(error, 'Could not grant sessions.'), 'error');
    } finally {
      setIsAdding(false);
    }
  };

  const handleVoid = async (grantId: string) => {
    setBusyGrantId(grantId);
    try {
      await reverseSessionGrant(grantId);
      await load();
      showToast('Grant voided.', 'success');
    } catch (error) {
      // The most common rejection is a void that would take back sessions already spent; the
      // server explains which, so surface its message rather than a generic one.
      showToast(errorMessage(error, 'Could not void this grant.'), 'error');
    } finally {
      setBusyGrantId(null);
    }
  };

  if (loadError) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-destructive">{loadError}</p>
        <Button type="button" variant="outline" size="sm" onClick={() => void load()}>
          Retry
        </Button>
      </div>
    );
  }

  if (!credit) {
    return <p className="text-sm text-muted-foreground">Loading session credit…</p>;
  }

  const { granted, used, remaining } = credit.balance;

  return (
    <div className="space-y-4">
      <div className="flex gap-6">
        {[
          { label: 'Granted', value: granted },
          { label: 'Used', value: used },
          { label: 'Remaining', value: remaining },
        ].map(({ label, value }) => (
          <div key={label}>
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
            <p className="text-lg font-semibold">{value}</p>
          </div>
        ))}
      </div>

      <p className="text-xs text-muted-foreground">
        Used counts sponsored consultations that have been sent and not canceled — it is derived
        from those engagements, not stored separately.
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <div className="w-28">
          <Label htmlFor="cc-grant-sessions">Sessions</Label>
          <Input
            id="cc-grant-sessions"
            type="number"
            min={1}
            max={1000}
            value={sessions}
            onChange={(event) => setSessions(event.target.value)}
            disabled={isAdding}
          />
        </div>
        <div className="min-w-[12rem] flex-1">
          <Label htmlFor="cc-grant-note">Note (optional)</Label>
          <Input
            id="cc-grant-note"
            value={note}
            maxLength={500}
            onChange={(event) => setNote(event.target.value)}
            disabled={isAdding}
          />
        </div>
        <Button type="button" onClick={() => void handleGrant()} disabled={isAdding}>
          {isAdding ? 'Adding…' : 'Add sessions'}
        </Button>
      </div>

      <SessionGrantList
        grants={credit.grants}
        busyGrantId={busyGrantId}
        onVoid={(grantId) => void handleVoid(grantId)}
      />
    </div>
  );
};
